import { useMemo } from 'react'
import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { useCurrentSiteTakeover, TakeoverOptions } from './useCurrentSiteTakeover'

type TakeoverBannerLocation = 'search' | 'offer' | 'generic'

interface UseShouldShowBannerOptions extends TakeoverOptions {
  /**
   * Location where the banner should be displayed.
   * For Lab payload, checks if this location is included in the payload's location array.
   * For custom config, checks the appropriate property (searchPage/offerPage/siteWide).
   */
  location?: TakeoverBannerLocation
}

/**
 * Hook to determine whether a banner should be shown.
 * Checks whether for a given location (search, offer, generic) the site takeover banner
 * should be shown (either lab or custom config)
 *
 * @param options - Configuration options for filtering banners
 * @param options.location - Location where banner should be displayed ('search' | 'offer' | 'generic')
 * @param options.vertical - Filter banners by specific vertical (e.g., 'flights', 'hotels')
 * @param options.offer - Filter banners by specific offer properties (type, campaign tags)
 * @param options.heroVertical - Filter banners for hero carousel sections by vertical
 * @returns True if a banner should be shown (either from Lab or custom config), false otherwise
 *
 * @example
 * ```tsx
 * // Check if search page banner should show
 * const shouldShowSearchBanner = useShouldShowBanner({ location: 'search' })
 *
 * // Check if offer page banner should show for hotels vertical
 * const shouldShowOfferBanner = useShouldShowBanner({ location: 'offer', vertical: 'hotel' })
 *
 * // Check if generic banner should show
 * const shouldShowGenericBanner = useShouldShowBanner({ location: 'generic' })
 * ```
 */
export function useShouldShowBanner(options: UseShouldShowBannerOptions = {}): boolean {
  const { location, ...takeoverOptions } = options
  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const customTakeover = useCurrentSiteTakeover(takeoverOptions)

  return useMemo(() => {
    if (!location) {
      return false
    }

    // Check Lab payload
    const labBannerContentPayload = enableSiteTakeoverBannerContent?.enabled
      ? enableSiteTakeoverBannerContent.value
      : undefined

    const hasLabBanner = isLabTakeoverBannerContentPayload(labBannerContentPayload) &&
      labBannerContentPayload.location.includes(location)

    // Check custom config
    let hasCustomBanner = false
    switch (location) {
      case 'search':
        hasCustomBanner = !!customTakeover?.searchPage?.bannerContent
        break
      case 'offer':
        hasCustomBanner = !!customTakeover?.offerPage?.bannerContent
        break
      case 'generic':
        hasCustomBanner = !!customTakeover?.siteWide?.banner
        break
    }

    return hasLabBanner || hasCustomBanner
  }, [enableSiteTakeoverBannerContent, customTakeover, location])
}
